import { useCallback } from "react";
import { useSignTransaction, useSuiClient, useCurrentAccount } from "@mysten/dapp-kit";
import type { Executor } from "@altheia-xyz/sui";

/**
 * Adapts the connected dapp-kit wallet into the SDK's Executor type so
 * provisionAgent()/revoke() run in the browser with the operator's wallet.
 *
 * Sign-then-execute (not useSignAndExecuteTransaction) so we control the
 * response options: the SDK's createdId() reads objectChanges and assertOk()
 * reads effects.status — both must be requested explicitly. waitForTransaction
 * mirrors the Node keypairExecutor so the 4-6 sequential provision txs don't
 * race on stale object versions.
 */
export function useOwnerExecutor(): Executor {
  const client = useSuiClient();
  const account = useCurrentAccount();
  const { mutateAsync: signTransaction } = useSignTransaction();

  return useCallback<Executor>(
    async (tx) => {
      if (account) tx.setSenderIfNotSet(account.address);
      const { bytes, signature } = await signTransaction({ transaction: tx });
      const res = await client.executeTransactionBlock({
        transactionBlock: bytes,
        signature,
        options: { showEffects: true, showObjectChanges: true, showEvents: true },
      });
      await client.waitForTransaction({ digest: res.digest });
      // Some wallet adapters return a response without objectChanges even when
      // requested; the SDK's createdId() needs them, so re-fetch if missing.
      // (This is the fix for the mint-succeeds-but-never-registers break.)
      if (!res.objectChanges || res.objectChanges.length === 0) {
        return await client.getTransactionBlock({
          digest: res.digest,
          options: { showEffects: true, showObjectChanges: true, showEvents: true },
        });
      }
      return res;
    },
    [client, account, signTransaction],
  );
}
